import { Colors, Icon } from '@amelio/ui-library';

import type { Step } from '@/components/Stepper/StepItem';
import type { IconName } from '@amelio/ui-library';

import { Stepper } from '@/components/Stepper/Stepper';

/** The badge icons, at the size the ring badge draws its own checkmark. */
const STEP_ICON_SIZE = 16;

/**
 * A stop on the path, named by the icon its badge carries rather than by the icon itself: the path
 * draws every badge at the one size and in the one colour its status calls for.
 */
export interface LevelsPathStep extends Omit<Step, 'icon' | 'status'> {
    /** Where the stop stands. Required here: the path reads the standing one from it. */
    status: NonNullable<Step['status']>;
    iconName?: IconName;
}

interface LevelsPathProps {
    /** The stops, in order; the one the employee stands on is the `active` one. */
    steps: LevelsPathStep[];
    ariaLabel: string;
    dataTestId: string;
}

/**
 * The stops between where the employee stands and the levels they are waiting for. Both waits the
 * summary knows -- an assessment held until its meeting, and an assessment that has yet to happen
 * -- draw the same path, so the shape is decided here and only the stops differ.
 */
export const LevelsPath = ({ steps, ariaLabel, dataTestId }: LevelsPathProps) => (
    <Stepper
        steps={steps.map(({ iconName, ...step }) => ({
            ...step,
            icon: iconName ? (
                <Icon
                    name={iconName}
                    size={STEP_ICON_SIZE}
                    color={step.status === 'active' ? Colors.WHITE : Colors.STEP_RAIL_IDLE}
                    ariaHidden
                />
            ) : undefined,
        }))}
        currentStep={steps.findIndex((step) => step.status === 'active')}
        orientation={'horizontal'}
        badgeAppearance={'ring'}
        // The path already walked is the employee's own turquoise; the step they stand on is
        // named in the violet its badge is drawn in, so the eye lands on the same stop twice.
        colors={{ active: Colors.STEP_RAIL_VIOLET, completedConnector: Colors.PRIMARY }}
        navAriaLabel={ariaLabel}
        getStepAriaLabel={(step) => step.label}
        dataTestId={dataTestId}
    />
);
